<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3';
import {
    AlignCenter,
    AlignJustify,
    AlignLeft,
    AlignRight,
    Bold,
    ChevronDown,
    Database,
    FileText,
    Heading1,
    Heading2,
    Heading3,
    Heading4,
    Heading5,
    Heading6,
    Italic,
    List,
    ListOrdered,
    MessageSquare,
    MessageSquarePlus,
    Minus,
    Quote,
    Redo,
    Scissors,
    Sigma,
    Strikethrough,
    Image,
    BarChart2,
    Table2,
    Type,
    Underline,
    Undo,
} from 'lucide-vue-next';
import { ref } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import type { FontSizePreset } from '@/lib/editor/font-size-extension';

defineProps<{
    editor: Editor | undefined;
    hasSelection?: boolean;
}>();

const emit = defineEmits<{
    'insert-table': [];
    'insert-figure': [];
    'insert-extraction-table': [];
    'insert-math-inline': [];
    'insert-math-block': [];
    'insert-footnote': [];
    'insert-toc': [];
    'insert-prisma': [];
    'insert-comment': [];
}>();

// ── Heading dropdown ────────────────────────────────────────────────────
const headingOpen = ref(false);
const fontSizeOpen = ref(false);
const colorOpen = ref(false);

const headingLevels = [
    { level: 1, icon: Heading1, label: 'Titre 1' },
    { level: 2, icon: Heading2, label: 'Titre 2' },
    { level: 3, icon: Heading3, label: 'Titre 3' },
    { level: 4, icon: Heading4, label: 'Titre 4' },
    { level: 5, icon: Heading5, label: 'Titre 5' },
    { level: 6, icon: Heading6, label: 'Titre 6' },
] as const;

const fontSizePresets: { preset: FontSizePreset; label: string }[] = [
    { preset: 'sm', label: 'Petit' },
    { preset: 'md', label: 'Normal' },
    { preset: 'lg', label: 'Grand' },
    { preset: 'xl', label: 'Très grand' },
    { preset: '2xl', label: 'Titre' },
];

const colorPresets = [
    { color: null, label: 'Défaut' },
    { color: '#111827', label: 'Noir' },
    { color: '#4B5563', label: 'Gris foncé' },
    { color: '#9CA3AF', label: 'Gris' },
    { color: '#2563EB', label: 'Bleu' },
    { color: '#7C3AED', label: 'Violet' },
    { color: '#DC2626', label: 'Rouge' },
    { color: '#D97706', label: 'Ambre' },
    { color: '#059669', label: 'Vert' },
];

function activeHeadingLevel(editor: Editor): number | null {
    for (let i = 1; i <= 6; i++) {
        if (editor.isActive('heading', { level: i })) {
            return i;
        }
    }

    return null;
}
</script>

<template>
    <div
        class="flex shrink-0 flex-wrap items-center gap-0.5 border-b border-border/40 bg-muted/20 px-4 py-1.5"
    >
        <!-- ── Historique ─────────────────────────────────────────────── -->
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor || !editor.can().undo()"
            title="Annuler"
            @click="editor?.chain().focus().undo().run()"
        >
            <Undo class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor || !editor.can().redo()"
            title="Rétablir"
            @click="editor?.chain().focus().redo().run()"
        >
            <Redo class="h-3.5 w-3.5" />
        </Button>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Titres (dropdown) ──────────────────────────────────────── -->
        <Popover v-model:open="headingOpen">
            <PopoverTrigger as-child>
                <Button
                    variant="ghost"
                    size="sm"
                    class="h-7 gap-1 px-2"
                    :class="{
                        'bg-accent text-accent-foreground':
                            editor && activeHeadingLevel(editor) !== null,
                    }"
                    :disabled="!editor"
                    title="Niveau de titre"
                >
                    <Type class="h-3.5 w-3.5" />
                    <span class="text-xs">
                        {{
                            editor && activeHeadingLevel(editor)
                                ? `H${activeHeadingLevel(editor)}`
                                : 'Titre'
                        }}
                    </span>
                    <ChevronDown class="h-3 w-3 opacity-60" />
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-36 p-1" align="start">
                <Button
                    v-for="h in headingLevels"
                    :key="h.level"
                    variant="ghost"
                    class="w-full justify-start gap-2 px-2 py-1.5"
                    :class="{
                        'bg-accent text-accent-foreground': editor?.isActive(
                            'heading',
                            { level: h.level },
                        ),
                    }"
                    @click="
                        editor
                            ?.chain()
                            .focus()
                            .toggleHeading({ level: h.level })
                            .run();
                        headingOpen = false;
                    "
                >
                    <component :is="h.icon" class="h-3.5 w-3.5 shrink-0" />
                    <span class="text-sm">{{ h.label }}</span>
                </Button>
                <div class="my-1 h-px bg-border/60" />
                <Button
                    variant="ghost"
                    class="w-full justify-start px-2 py-1.5 text-sm"
                    @click="
                        editor?.chain().focus().setParagraph().run();
                        headingOpen = false;
                    "
                >
                    Paragraphe
                </Button>
            </PopoverContent>
        </Popover>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Style de texte ─────────────────────────────────────────── -->
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground': editor?.isActive('bold'),
            }"
            :disabled="!editor"
            title="Gras"
            @click="editor?.chain().focus().toggleBold().run()"
        >
            <Bold class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground': editor?.isActive('italic'),
            }"
            :disabled="!editor"
            title="Italique"
            @click="editor?.chain().focus().toggleItalic().run()"
        >
            <Italic class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground':
                    editor?.isActive('underline'),
            }"
            :disabled="!editor"
            title="Souligné"
            @click="editor?.chain().focus().toggleUnderline().run()"
        >
            <Underline class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground': editor?.isActive('strike'),
            }"
            :disabled="!editor"
            title="Barré"
            @click="editor?.chain().focus().toggleStrike().run()"
        >
            <Strikethrough class="h-3.5 w-3.5" />
        </Button>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Alignement ─────────────────────────────────────────────── -->
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground': editor?.isActive({
                    textAlign: 'left',
                }),
            }"
            :disabled="!editor"
            title="Aligner à gauche"
            @click="editor?.chain().focus().setTextAlign('left').run()"
        >
            <AlignLeft class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground': editor?.isActive({
                    textAlign: 'center',
                }),
            }"
            :disabled="!editor"
            title="Centrer"
            @click="editor?.chain().focus().setTextAlign('center').run()"
        >
            <AlignCenter class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground': editor?.isActive({
                    textAlign: 'right',
                }),
            }"
            :disabled="!editor"
            title="Aligner à droite"
            @click="editor?.chain().focus().setTextAlign('right').run()"
        >
            <AlignRight class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground': editor?.isActive({
                    textAlign: 'justify',
                }),
            }"
            :disabled="!editor"
            title="Justifier"
            @click="editor?.chain().focus().setTextAlign('justify').run()"
        >
            <AlignJustify class="h-3.5 w-3.5" />
        </Button>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Taille de police (dropdown) ───────────────────────────── -->
        <Popover v-model:open="fontSizeOpen">
            <PopoverTrigger as-child>
                <Button
                    variant="ghost"
                    size="sm"
                    class="h-7 gap-1 px-2"
                    :disabled="!editor"
                    title="Taille du texte"
                >
                    <span class="text-xs">Aa</span>
                    <ChevronDown class="h-3 w-3 opacity-60" />
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-36 p-1" align="start">
                <Button
                    v-for="s in fontSizePresets"
                    :key="s.preset"
                    variant="ghost"
                    class="w-full justify-start px-2 py-1.5"
                    :class="{
                        'bg-accent text-accent-foreground': editor?.isActive(
                            'fontSize',
                            { preset: s.preset },
                        ),
                    }"
                    @click="
                        editor?.chain().focus().setFontSize(s.preset).run();
                        fontSizeOpen = false;
                    "
                >
                    <span class="text-sm">{{ s.label }}</span>
                </Button>
                <div class="my-1 h-px bg-border/60" />
                <Button
                    variant="ghost"
                    class="w-full justify-start px-2 py-1.5 text-sm"
                    @click="
                        editor?.chain().focus().unsetFontSize().run();
                        fontSizeOpen = false;
                    "
                >
                    Réinitialiser
                </Button>
            </PopoverContent>
        </Popover>

        <!-- ── Couleur de texte ───────────────────────────────────────── -->
        <Popover v-model:open="colorOpen">
            <PopoverTrigger as-child>
                <Button
                    variant="ghost"
                    size="icon"
                    class="h-7 w-7"
                    :disabled="!editor"
                    title="Couleur du texte"
                >
                    <div class="flex flex-col items-center gap-0.5">
                        <span class="text-[11px] leading-none font-bold"
                            >A</span
                        >
                        <div
                            class="h-0.5 w-3.5 rounded-full"
                            :style="{
                                backgroundColor:
                                    editor?.getAttributes('textStyle').color ??
                                    '#111827',
                            }"
                        />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-44 p-2" align="start">
                <p class="mb-2 text-xs font-medium text-foreground">
                    Couleur du texte
                </p>
                <div class="grid grid-cols-5 gap-1">
                    <button
                        v-for="c in colorPresets"
                        :key="c.label"
                        :title="c.label"
                        class="h-6 w-6 rounded-md border border-border/60 transition-transform hover:scale-110"
                        :style="{ backgroundColor: c.color ?? 'transparent' }"
                        :class="c.color === null ? 'bg-muted' : ''"
                        @click="
                            c.color
                                ? editor
                                      ?.chain()
                                      .focus()
                                      .setColor(c.color)
                                      .run()
                                : editor?.chain().focus().unsetColor().run();
                            colorOpen = false;
                        "
                    />
                </div>
            </PopoverContent>
        </Popover>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Listes et blocs ────────────────────────────────────────── -->
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground':
                    editor?.isActive('bulletList'),
            }"
            :disabled="!editor"
            title="Liste à puces"
            @click="editor?.chain().focus().toggleBulletList().run()"
        >
            <List class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground':
                    editor?.isActive('orderedList'),
            }"
            :disabled="!editor"
            title="Liste numérotée"
            @click="editor?.chain().focus().toggleOrderedList().run()"
        >
            <ListOrdered class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :class="{
                'bg-accent text-accent-foreground':
                    editor?.isActive('blockquote'),
            }"
            :disabled="!editor"
            title="Bloc de citation"
            @click="editor?.chain().focus().toggleBlockquote().run()"
        >
            <Quote class="h-3.5 w-3.5" />
        </Button>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Commentaire ────────────────────────────────────────────── -->
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor || !hasSelection"
            :title="
                hasSelection
                    ? 'Commenter la sélection'
                    : 'Sélectionnez du texte pour commenter'
            "
            @click="emit('insert-comment')"
        >
            <MessageSquarePlus class="h-3.5 w-3.5" />
        </Button>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Insertion ──────────────────────────────────────────────── -->
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Séparateur horizontal"
            @click="editor?.chain().focus().setHorizontalRule().run()"
        >
            <Minus class="h-3.5 w-3.5" />
        </Button>
        <Button
            data-tour="editor-page-break"
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Saut de page"
            @click="editor?.chain().focus().insertPageBreak().run()"
        >
            <Scissors class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="sm"
            class="h-7 px-2"
            :disabled="!editor"
            title="Espace vertical"
            @click="editor?.chain().focus().insertVerticalSpacer().run()"
        >
            <span class="text-[10px]">↕</span>
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Insérer un tableau"
            @click="emit('insert-table')"
        >
            <Table2 class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Insérer une figure"
            @click="emit('insert-figure')"
        >
            <Image class="h-3.5 w-3.5" />
        </Button>
        <Button
            data-tour="editor-insert-extraction"
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Insérer un tableau d'extraction"
            @click="emit('insert-extraction-table')"
        >
            <Database class="h-3.5 w-3.5" />
        </Button>

        <div class="mx-1 h-4 w-px bg-border/60" />

        <!-- ── Math, Footnotes, TOC ───────────────────────────────────── -->
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Insérer une formule mathématique (inline)"
            @click="emit('insert-math-inline')"
        >
            <Sigma class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="sm"
            class="h-7 px-2"
            :disabled="!editor"
            title="Insérer un bloc mathématique"
            @click="emit('insert-math-block')"
        >
            <span class="font-mono text-[10px] font-bold">∑</span>
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Insérer une note de bas de page"
            @click="emit('insert-footnote')"
        >
            <MessageSquare class="h-3.5 w-3.5" />
        </Button>
        <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Insérer une table des matières"
            @click="emit('insert-toc')"
        >
            <FileText class="h-3.5 w-3.5" />
        </Button>
        <Button
            data-tour="editor-insert-prisma"
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            :disabled="!editor"
            title="Insérer un diagramme PRISMA"
            @click="emit('insert-prisma')"
        >
            <BarChart2 class="h-3.5 w-3.5" />
        </Button>
    </div>
</template>
